/**
 * WalletSpendMeters — compact percent-bar strip for the organization's pooled
 * spending limits (2026-08-14).
 *
 * One labeled bar per configured pool window, fed by the live
 * rbacGetAccountSpendLimits windowsStatus. Renders NOTHING when the viewer
 * lacks usage.pool-limits.view/manage, when no limits are configured, or when
 * the read fails — the strip is pure garnish, never a wall.
 *
 * It only draws; its behaviour is useWalletSpendMeters (wallet part 2a, 2026-10-02).
 */

import React from 'react';
import styled from 'styled-components';
import { useWalletSpendMeters, MeterTone } from '../../hooks/useWalletSpendMeters';

const GOLD = 'var(--brand-gold)';
const AMBER = '#D68910';
const RED = '#C0392B';

const Card = styled.section`
  background: white;
  border: 1px solid var(--brand-line);
  border-radius: 1rem;
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.04);
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    padding: 1.25rem 1.5rem;
    margin-bottom: 2rem;
  }
`;

const Title = styled.h3`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--brand-text);
  margin: 0 0 0.75rem;
  text-transform: lowercase;
`;

const MeterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MeterRow = styled.div``;

const MeterHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const MeterLabel = styled.span`
  font-size: 0.8125rem;
  color: var(--brand-text);
  text-transform: lowercase;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MeterFigures = styled.span`
  flex: none;
  font-size: 0.75rem;
  color: #6b7280;
  text-align: right;
  font-variant-numeric: tabular-nums;
  text-transform: lowercase;
`;

const Track = styled.div`
  height: 6px;
  border-radius: 3px;
  background: #F0EEE8;
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${(p) => Math.min(100, Math.max(0, p.$pct))}%;
  background: ${(p) => p.$color};
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const TONE_COLOR: Record<MeterTone, string> = { ok: GOLD, near: AMBER, over: RED };

const WalletSpendMeters: React.FC = () => {
  const meters = useWalletSpendMeters();

  if (meters.length === 0) return null;

  return (
    <Card aria-label="organization spending limits">
      <Title>organization spending limits</Title>
      <MeterList>
        {meters.map((m) => (
          <MeterRow key={m.id}>
            <MeterHead>
              <MeterLabel>{m.label}</MeterLabel>
              <MeterFigures>{m.figures}</MeterFigures>
            </MeterHead>
            <Track
              role="meter"
              aria-valuenow={m.percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${m.label}: ${m.percent}% of limit used`}
            >
              <Fill $pct={m.fillPct} $color={TONE_COLOR[m.tone]} />
            </Track>
          </MeterRow>
        ))}
      </MeterList>
    </Card>
  );
};

export default WalletSpendMeters;
